import { useCallback, useMemo } from 'react';
import type { Key } from 'react-aria-components';

import { Button } from '@/lib/base/buttons/button';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import { cx } from '@/utils/cx';

import {
  isTriggerButtonRecordOpen,
  OPEN_RECORD_CLASSES,
  type TriggerButtonDescriptor,
  type TriggerButtonTarget,
} from './ActionOverview.helpers';

interface TriggerOverflowButtonProps {
  /** The overflowed descriptors (already sliced past MAX_VISIBLE_BUTTONS). Non-empty. */
  overflowButtons: TriggerButtonDescriptor[];
  onActivate: (target: TriggerButtonTarget) => void;
}

/**
 * The "+N" overflow control and its menu. Reads the slideout stack INSIDE
 * this leaf (mirroring TriggerActionButton) so stack changes re-render only
 * this subtree, not the whole header row, and applies the open-record
 * treatment to both the "+N" button and the matching menu row.
 */
export function TriggerOverflowButton({
  overflowButtons,
  onActivate,
}: TriggerOverflowButtonProps) {
  const slideoutStack = useSlideoutStackContextOptional();
  const stack = slideoutStack?.stack;

  const openKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const descriptor of overflowButtons) {
      if (isTriggerButtonRecordOpen(descriptor.target, stack)) {
        keys.add(descriptor.key);
      }
    }
    return keys;
  }, [overflowButtons, stack]);

  const anyOpen = openKeys.size > 0;

  const handleAction = useCallback(
    (key: Key) => {
      const descriptor = overflowButtons.find(b => b.key === key);
      if (descriptor) {
        onActivate(descriptor.target);
      }
    },
    [overflowButtons, onActivate]
  );

  return (
    <Dropdown.Root instance='trigger-shortcut-overflow'>
      <Button
        color='secondary'
        size='xs'
        aria-label={`Show ${overflowButtons.length} more`}
        aria-pressed={anyOpen}
        className={cx(anyOpen && OPEN_RECORD_CLASSES)}
      >
        +{overflowButtons.length}
      </Button>
      <Dropdown.Popover placement='bottom right' className='min-w-[200px]'>
        <Dropdown.Menu selectionMode='none' onAction={handleAction}>
          {overflowButtons.map(descriptor => (
            <Dropdown.Item
              key={descriptor.key}
              id={descriptor.key}
              label={descriptor.label}
              icon={descriptor.icon}
              isActive={openKeys.has(descriptor.key)}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
}
